// Who may open a store — docs/plans/marketplace-commerce.md §7 and M-D6, identity plan §13.1 (`create_business`).
import type { AgeBand } from "../identity/types";

/**
 * The seller terms every store owner accepts. The version is recorded with each acceptance; changing the terms
 * means a new version, and owners re-accept before creating or publishing another store.
 */
export const SELLER_TERMS_VERSION = "2026-09-14";

/** Plain-language summary shown at /sell. ⚖️ Pending legal review before paid sales launch. */
export const SELLER_TERMS_POINTS: readonly string[] = [
  "You own, or have the rights to sell, everything you list.",
  "Paid sales launch soon — until then every product you publish is free.",
  "Every listing is checked before it goes live, and listings that break the rules can be removed.",
  "Buyers see whether you sell as a trader or as a private person.",
];

export type TraderDeclaration = "trader" | "non_trader";
export const TRADER_DECLARATIONS: readonly TraderDeclaration[] = ["trader", "non_trader"];

/** The eligibility requirements checked before a store is created or published, in the order they're resolved. */
export type SellRequirement = "verified_email" | "age_18_plus" | "seller_terms" | "trader_declaration";

export const SELL_REQUIREMENTS: readonly SellRequirement[] = [
  "verified_email",
  "age_18_plus",
  "seller_terms",
  "trader_declaration",
];

export interface SellerAcceptance {
  termsVersion: string;
  traderDeclaration: TraderDeclaration;
}

export interface SellEligibilityInput {
  emailVerified: boolean;
  ageBand: AgeBand | null;
  /** The Person's latest acceptance, if any. */
  acceptance: SellerAcceptance | null;
}

/**
 * Missing requirements, in resolution order. Age comes from the self-declared band set once at identity claim
 * (M-D6); an unknown band never passes. Terms count only for the current version.
 */
export function missingSellRequirements({ emailVerified, ageBand, acceptance }: SellEligibilityInput): SellRequirement[] {
  const missing: SellRequirement[] = [];
  if (!emailVerified) missing.push("verified_email");
  if (ageBand !== "18_plus") missing.push("age_18_plus");
  if (acceptance?.termsVersion !== SELLER_TERMS_VERSION) missing.push("seller_terms");
  if (!acceptance || !TRADER_DECLARATIONS.includes(acceptance.traderDeclaration)) missing.push("trader_declaration");
  return missing;
}

/** Requirements the Person can't resolve at /sell: terms and the trader declaration are answered there. */
export function blockingSellRequirements(missing: readonly SellRequirement[]): SellRequirement[] {
  return missing.filter((requirement) => requirement === "verified_email" || requirement === "age_18_plus");
}

/** Plan §14.1: store creation is rate-limited per Person. */
export const STORES_PER_DAY_LIMIT = 3;
